import { PDFDocument, rgb } from "pdf-lib";
import templateUrl from './template.pdf';
import { drawLogos } from "@/reports/shared_utils/drawLogos";
import { collectUnsupportedHighlights, drawUnsupportedHighlights } from "@/reports/shared_utils/highlightUnsupported";
import { sanitizeForPdf } from "@/reports/shared_utils/sanitizeForPdf";

// Maps each schema key to the representative PDF field name used for
// highlight positioning in the preview pane.
export const SCHEMA_TO_PDF_FIELD = {
  date: "date", studentName: "studentName1", oen: "oen1", grade: "grade1",
  gradeInSeptember: "gradeInSeptember", board: "board", school: "school",
  address1: "address1", address2: "address2", principal: "principal",
  telephone: "telephone", teacher: "teacher",
  daysAbsent: "daysAbsent", totalDaysAbsent: "totalDaysAbsent",
  timesLate: "timesLate", totalTimesLate: "totalTimesLate",
  responsibility1: "responsibility1", responsibility2: "responsibility2",
  organization1: "organization1", organization2: "organization2",
  independentWork1: "independentWork1", independentWork2: "independentWork2",
  collaboration1: "collaboration1", collaboration2: "collaboration2",
  initiative1: "initiative1", initiative2: "initiative2",
  selfRegulation1: "selfRegulation1", selfRegulation2: "selfRegulation2",
  strengthsNextStepsLSWH: "strengthsNextStepsForImprovementLSWH",
  languagePlan: "languageESLELD", languageGrade1: "languageGrade1", languageGrade2: "languageGrade2",
  languageComments: "languageComments",
  nativeLanguage: "nativeLanguage", nativeLanguagePlan: "nativeLanguageESLELD",
  nativeLanguageGrade1: "nativeLanguageGrade1", nativeLanguageGrade2: "nativeLanguageGrade2",
  nativeLanguageComments: "nativeLanguageComments",
  frenchPlan: "frenchNA",
  frenchListeningPlan: "frenchListeningESLELD", frenchListeningGrade1: "frenchListeningGrade1", frenchListeningGrade2: "frenchListeningGrade2",
  frenchSpeakingPlan: "frenchSpeakingESLELD",  frenchSpeakingGrade1: "frenchSpeakingGrade1",  frenchSpeakingGrade2: "frenchSpeakingGrade2",
  frenchReadingPlan: "frenchReadingESLELD",    frenchReadingGrade1: "frenchReadingGrade1",    frenchReadingGrade2: "frenchReadingGrade2",
  frenchWritingPlan: "frenchWritingESLELD",    frenchWritingGrade1: "frenchWritingGrade1",    frenchWritingGrade2: "frenchWritingGrade2",
  frenchComments: "frenchComments",
  mathematicsPlan: "mathematicsESLELD", mathematicsGrade1: "mathematicsGrade1", mathematicsGrade2: "mathematicsGrade2",
  mathematicsComments: "mathematicsComments",
  scienceAndTechnologyPlan: "scienceAndTechnologyESLELD", scienceAndTechnologyGrade1: "scienceAndTechnologyGrade1",
  scienceAndTechnologyGrade2: "scienceAndTechnologyGrade2", scienceAndTechnologyComments: "scienceAndTechnologyComments",
  socialStudiesPlan: "socialStudiesESLELD", socialStudiesGrade1: "socialStudiesGrade1",
  socialStudiesGrade2: "socialStudiesGrade2", socialStudiesComments: "socialStudiesComments",
  healthEducationPlan: "healthEducationESLELD", healthEducationGrade1: "healthEducationGrade1", healthEducationGrade2: "healthEducationGrade2",
  physicalEducationPlan: "physicalEducationESLELD", physicalEducationGrade1: "physicalEducationGrade1", physicalEducationGrade2: "physicalEducationGrade2",
  healthAndPhysicalEducationComments: "healthAndPhysicalEducationComments",
  artsHeader: "artsHeader",
  arts1: "arts1", arts1Plan: "arts1ESLELD", arts1Grade1: "arts1Grade1", arts1Grade2: "arts1Grade2",
  arts2: "arts2", arts2Plan: "arts2ESLELD", arts2Grade1: "arts2Grade1", arts2Grade2: "arts2Grade2",
  arts3: "arts3", arts3Plan: "arts3ESLELD", arts3Grade1: "arts3Grade1", arts3Grade2: "arts3Grade2",
  arts4: "arts4", arts4Plan: "arts4ESLELD", arts4Grade1: "arts4Grade1", arts4Grade2: "arts4Grade2",
  optionalStudies: "optionalStudies", optionalStudiesPlan: "optionalStudiesESLELD",
  optionalStudiesGrade1: "optionalStudiesGrade1", optionalStudiesGrade2: "optionalStudiesGrade2",
  optionalStudiesComments: "optionalStudiesComments",
  ersCompleted: "ersCompletedYes", ersDateYear: "ersDateYear",
  ersDateMonth: "ersDateMonth", ersDateDay: "ersDateDay",
  ersBenchmark: "ersBenchmarkYes",
}

// PDF page index (0-based) for each schema field (provincial has 4 pages).
export const PAGE_INDEX_FOR_SCHEMA_FIELD = {
  date: 0, studentName: 0, oen: 0, grade: 0, gradeInSeptember: 0, teacher: 0,
  board: 0, school: 0, address1: 0, address2: 0, principal: 0, telephone: 0,
  daysAbsent: 0, totalDaysAbsent: 0, timesLate: 0, totalTimesLate: 0,
  responsibility1: 0, responsibility2: 0, organization1: 0, organization2: 0,
  independentWork1: 0, independentWork2: 0, collaboration1: 0, collaboration2: 0,
  initiative1: 0, initiative2: 0, selfRegulation1: 0, selfRegulation2: 0,
  strengthsNextStepsLSWH: 0,
  languagePlan: 1, languageGrade1: 1, languageGrade2: 1, languageComments: 1,
  nativeLanguage: 1, nativeLanguagePlan: 1, nativeLanguageGrade1: 1, nativeLanguageGrade2: 1, nativeLanguageComments: 1,
  frenchPlan: 1, frenchListeningPlan: 1, frenchListeningGrade1: 1, frenchListeningGrade2: 1,
  frenchSpeakingPlan: 1, frenchSpeakingGrade1: 1, frenchSpeakingGrade2: 1,
  frenchReadingPlan: 1, frenchReadingGrade1: 1, frenchReadingGrade2: 1,
  frenchWritingPlan: 1, frenchWritingGrade1: 1, frenchWritingGrade2: 1,
  frenchComments: 1,
  mathematicsPlan: 1, mathematicsGrade1: 1, mathematicsGrade2: 1, mathematicsComments: 1,
  scienceAndTechnologyPlan: 1, scienceAndTechnologyGrade1: 1, scienceAndTechnologyGrade2: 1, scienceAndTechnologyComments: 1,
  socialStudiesPlan: 2, socialStudiesGrade1: 2, socialStudiesGrade2: 2, socialStudiesComments: 2,
  healthEducationPlan: 2, healthEducationGrade1: 2, healthEducationGrade2: 2,
  physicalEducationPlan: 2, physicalEducationGrade1: 2, physicalEducationGrade2: 2,
  healthAndPhysicalEducationComments: 2,
  artsHeader: 2, arts1: 2, arts1Plan: 2, arts1Grade1: 2, arts1Grade2: 2,
  arts2: 2, arts2Plan: 2, arts2Grade1: 2, arts2Grade2: 2,
  arts3: 2, arts3Plan: 2, arts3Grade1: 2, arts3Grade2: 2,
  arts4: 2, arts4Plan: 2, arts4Grade1: 2, arts4Grade2: 2,
  artsComments: 2,
  optionalStudies: 2, optionalStudiesPlan: 2, optionalStudiesGrade1: 2, optionalStudiesGrade2: 2, optionalStudiesComments: 2,
  ersCompleted: 2, ersDateYear: 2, ersDateMonth: 2, ersDateDay: 2, ersBenchmark: 2,
}

// One representative PDF field per step - used to compute the scroll
// position in the preview pane when the user changes steps.
export const STEP_ANCHOR_FIELDS = {
  1: "studentName1",         2: "board",                    3: "responsibility1",
  4: "languageGrade1",       5: "frenchListeningGrade1",    6: "mathematicsGrade1",
  7: "scienceAndTechnologyGrade1", 8: "socialStudiesGrade1",
  9: "healthEducationGrade1", 10: "artsHeader",             11: "ersCompletedYes",
}

const val = (v) => sanitizeForPdf(v);

const fill = async (pdfFormObject, highlightRect = null, logos = []) => {
  // load pdf
  const existingPdfBytes = await fetch(templateUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const pdfForm = pdfDoc.getForm();

  //Uncomment to display all pdfForm fields in console log
  // const fields = pdfForm.getFields();
  // fields.forEach((field) => {
  //   const type = field.constructor.name;
  //   const name = field.getName();
  //   console.log(`${name}`);
  // });
  // console.log(pdfFormObject);

  ////////////// FORM VARIABLES TO PDF FIELDS TRANSLATION
  var field;
  ////////// Variables on the LEFT are PDF Fields
  ////////// Variables on the RIGHT are Form Schema Variables
  field = pdfForm.getField("date");
  field.setText(val(pdfFormObject["date"]));
  field.setFontSize(8);

  field = pdfForm.getField("studentName1");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(8);

  field = pdfForm.getField("oen1");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(8);

  field = pdfForm.getField("daysAbsent");
  field.setText(val(pdfFormObject["daysAbsent"]));
  field.setFontSize(8);

  field = pdfForm.getField("totalDaysAbsent");
  field.setText(val(pdfFormObject["totalDaysAbsent"]));
  field.setFontSize(8);

  field = pdfForm.getField("timesLate");
  field.setText(val(pdfFormObject["timesLate"]));
  field.setFontSize(8);

  field = pdfForm.getField("totalTimesLate");
  field.setText(val(pdfFormObject["totalTimesLate"]));
  field.setFontSize(8);

  field = pdfForm.getField("teacher");
  field.setText(val(pdfFormObject["teacher"]));
  field.setFontSize(8);

  field = pdfForm.getField("grade1");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(8);

  field = pdfForm.getField("board");
  field.setText(val(pdfFormObject["board"]));
  field.setFontSize(8);

  field = pdfForm.getField("school");
  field.setText(val(pdfFormObject["school"]));
  field.setFontSize(8);

  field = pdfForm.getField("address1");
  field.setText(val(pdfFormObject["address1"]));
  field.setFontSize(8);

  field = pdfForm.getField("address2");
  field.setText(val(pdfFormObject["address2"]));
  field.setFontSize(8);

  field = pdfForm.getField("principal");
  field.setText(val(pdfFormObject["principal"]));
  field.setFontSize(8);

  field = pdfForm.getField("telephone");
  field.setText(val(pdfFormObject["telephone"]));
  field.setFontSize(8);

  field = pdfForm.getField("gradeInSeptember");
  field.setText(val(pdfFormObject["gradeInSeptember"]));
  field.setFontSize(12);


  field = pdfForm.getField("responsibility1");
  field.setText(val(pdfFormObject["responsibility1"]));
  field.setFontSize(11);

  field = pdfForm.getField("responsibility2");
  field.setText(val(pdfFormObject["responsibility2"]));
  field.setFontSize(11);

  field = pdfForm.getField("organization1");
  field.setText(val(pdfFormObject["organization1"]));
  field.setFontSize(11);

  field = pdfForm.getField("organization2");
  field.setText(val(pdfFormObject["organization2"]));
  field.setFontSize(11);

  field = pdfForm.getField("independentWork1");
  field.setText(val(pdfFormObject["independentWork1"]));
  field.setFontSize(11);

  field = pdfForm.getField("independentWork2");
  field.setText(val(pdfFormObject["independentWork2"]));
  field.setFontSize(11);

  field = pdfForm.getField("collaboration1");
  field.setText(val(pdfFormObject["collaboration1"]));
  field.setFontSize(11);

  field = pdfForm.getField("collaboration2");
  field.setText(val(pdfFormObject["collaboration2"]));
  field.setFontSize(11);

  field = pdfForm.getField("initiative1");
  field.setText(val(pdfFormObject["initiative1"]));
  field.setFontSize(11);

  field = pdfForm.getField("initiative2");
  field.setText(val(pdfFormObject["initiative2"]));
  field.setFontSize(11);

  field = pdfForm.getField("selfRegulation1");
  field.setText(val(pdfFormObject["selfRegulation1"]));
  field.setFontSize(11);

  field = pdfForm.getField("selfRegulation2");
  field.setText(val(pdfFormObject["selfRegulation2"]));
  field.setFontSize(11);

  field = pdfForm.getField("selfRegulation1");
  field.setText(val(pdfFormObject["selfRegulation1"]));
  field.setFontSize(11);

  field = pdfForm.getField("strengthsNextStepsForImprovementLSWH");
  field.setText(val(pdfFormObject["strengthsNextStepsLSWH"]));
  field.setFontSize(8);
  field.enableMultiline();

  // SECOND PAGE
  field = pdfForm.getField("studentName2");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(9);
  field = pdfForm.getField("oen2");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(9);
  field = pdfForm.getField("grade2");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(9);

  // LANGUAGE
  field = pdfForm.getField("languageESLELD");
  pdfFormObject["languagePlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("languageIEP");
  pdfFormObject["languagePlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("languageNA");
  pdfFormObject["languagePlan"] === "NA" ? field.check() : field.uncheck();
  //
  field = pdfForm.getField("languageGrade1");
  field.setText(val(pdfFormObject["languageGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("languageGrade2");
  field.setText(val(pdfFormObject["languageGrade2"]));
  field.setFontSize(9);
  //
  field = pdfForm.getField("languageComments");
  field.setText(val(pdfFormObject["languageComments"]));
  field.setFontSize(8);
  field.enableMultiline();


  // FRENCH
  field = pdfForm.getField("frenchNA");
  pdfFormObject["frenchPlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchCore");
  pdfFormObject["frenchPlan"] === "Core" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchImmersion");
  pdfFormObject["frenchPlan"] === "Immersion" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchExtended");
  pdfFormObject["frenchPlan"] === "Extended" ? field.check() : field.uncheck();

  //
  field = pdfForm.getField("frenchListeningESLELD");
  pdfFormObject["frenchListeningPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchListeningIEP");
  pdfFormObject["frenchListeningPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchListeningGrade1");
  field.setText(val(pdfFormObject["frenchListeningGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchListeningGrade2");
  field.setText(val(pdfFormObject["frenchListeningGrade2"]));
  field.setFontSize(9);

  //
  field = pdfForm.getField("frenchSpeakingESLELD");
  pdfFormObject["frenchSpeakingPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchSpeakingIEP");
  pdfFormObject["frenchSpeakingPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchSpeakingGrade1");
  field.setText(val(pdfFormObject["frenchSpeakingGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchSpeakingGrade2");
  field.setText(val(pdfFormObject["frenchSpeakingGrade2"]));
  field.setFontSize(9);

  //
  field = pdfForm.getField("frenchReadingESLELD");
  pdfFormObject["frenchReadingPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchReadingIEP");
  pdfFormObject["frenchReadingPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchReadingGrade1");
  field.setText(val(pdfFormObject["frenchReadingGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchReadingGrade2");
  field.setText(val(pdfFormObject["frenchReadingGrade2"]));
  field.setFontSize(9);

  //
  field = pdfForm.getField("frenchWritingESLELD");
  pdfFormObject["frenchWritingPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchWritingIEP");
  pdfFormObject["frenchWritingPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("frenchWritingGrade1");
  field.setText(val(pdfFormObject["frenchWritingGrade1"]));
  field.setFontSize(9);
  field = pdfForm.getField("frenchWritingGrade2");
  field.setText(val(pdfFormObject["frenchWritingGrade2"]));
  field.setFontSize(9);

  //
  field = pdfForm.getField("frenchComments");
  field.setText(val(pdfFormObject["frenchComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  // NATIVE LANGUAGE
field = pdfForm.getField("nativeLanguage");
field.setText(val(pdfFormObject["nativeLanguage"]));
field.setFontSize(9);
field = pdfForm.getField("nativeLanguageESLELD");
pdfFormObject["nativeLanguagePlan"] === "ESLELD" ? field.check() : field.uncheck();
field = pdfForm.getField("nativeLanguageIEP");
pdfFormObject["nativeLanguagePlan"] === "IEP" ? field.check() : field.uncheck();
field = pdfForm.getField("nativeLanguageNA");
pdfFormObject["nativeLanguagePlan"] === "NA" ? field.check() : field.uncheck();

field = pdfForm.getField("nativeLanguageGrade1");
field.setText(val(pdfFormObject["nativeLanguageGrade1"]));
field.setFontSize(9);
field = pdfForm.getField("nativeLanguageGrade2");
field.setText(val(pdfFormObject["nativeLanguageGrade2"]));
field.setFontSize(9);

field = pdfForm.getField("nativeLanguageComments");
field.setText(val(pdfFormObject["nativeLanguageComments"]));
field.setFontSize(8);
field.enableMultiline();

// MATHEMATICS
field = pdfForm.getField("mathematicsESLELD");
pdfFormObject["mathematicsPlan"] === "ESLELD" ? field.check() : field.uncheck();
field = pdfForm.getField("mathematicsIEP");
pdfFormObject["mathematicsPlan"] === "IEP" ? field.check() : field.uncheck();
field = pdfForm.getField("mathematicsFrench");
pdfFormObject["mathematicsPlan"] === "French" ? field.check() : field.uncheck();

field = pdfForm.getField("mathematicsGrade1");
field.setText(val(pdfFormObject["mathematicsGrade1"]));
field.setFontSize(9);
field = pdfForm.getField("mathematicsGrade2");
field.setText(val(pdfFormObject["mathematicsGrade2"]));
field.setFontSize(9);

field = pdfForm.getField("mathematicsComments");
field.setText(val(pdfFormObject["mathematicsComments"]));
field.setFontSize(8);
field.enableMultiline();


// SCIENCE AND TECHNOLOGY
field = pdfForm.getField("scienceAndTechnologyESLELD");
pdfFormObject["scienceAndTechnologyPlan"] === "ESLELD" ? field.check() : field.uncheck();
field = pdfForm.getField("scienceAndTechnologyIEP");
pdfFormObject["scienceAndTechnologyPlan"] === "IEP" ? field.check() : field.uncheck();
field = pdfForm.getField("scienceAndTechnologyFrench");
pdfFormObject["scienceAndTechnologyPlan"] === "French" ? field.check() : field.uncheck();

field = pdfForm.getField("scienceAndTechnologyGrade1");
field.setText(val(pdfFormObject["scienceAndTechnologyGrade1"]));
field.setFontSize(9);
field = pdfForm.getField("scienceAndTechnologyGrade2");
field.setText(val(pdfFormObject["scienceAndTechnologyGrade2"]));
field.setFontSize(9);

field = pdfForm.getField("scienceAndTechnologyComments");
field.setText(val(pdfFormObject["scienceAndTechnologyComments"]));
field.setFontSize(8);
field.enableMultiline();





  // THIRD PAGE
  field = pdfForm.getField("studentName3");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(9);
  field = pdfForm.getField("oen3");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(9);
  field = pdfForm.getField("grade3");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(9);


// SOCIAL STUDIES
field = pdfForm.getField("socialStudiesESLELD");
pdfFormObject["socialStudiesPlan"] === "ESLELD" ? field.check() : field.uncheck();
field = pdfForm.getField("socialStudiesIEP");
pdfFormObject["socialStudiesPlan"] === "IEP" ? field.check() : field.uncheck();
field = pdfForm.getField("socialStudiesFrench");
pdfFormObject["socialStudiesPlan"] === "French" ? field.check() : field.uncheck();

field = pdfForm.getField("socialStudiesGrade1");
field.setText(val(pdfFormObject["socialStudiesGrade1"]));
field.setFontSize(9);
field = pdfForm.getField("socialStudiesGrade2");
field.setText(val(pdfFormObject["socialStudiesGrade2"]));
field.setFontSize(9);

field = pdfForm.getField("socialStudiesComments");
field.setText(val(pdfFormObject["socialStudiesComments"]));
field.setFontSize(8);
field.enableMultiline();


// HEALTH EDUCATION
field = pdfForm.getField("healthEducationESLELD");
pdfFormObject["healthEducationPlan"] === "ESLELD" ? field.check() : field.uncheck();
field = pdfForm.getField("healthEducationIEP");
pdfFormObject["healthEducationPlan"] === "IEP" ? field.check() : field.uncheck();
field = pdfForm.getField("healthEducationFrench");
pdfFormObject["healthEducationPlan"] === "French" ? field.check() : field.uncheck();



field = pdfForm.getField("healthEducationGrade1");
field.setText(val(pdfFormObject["healthEducationGrade1"]));
field.setFontSize(9);


field = pdfForm.getField("healthEducationGrade2");
field.setText(val(pdfFormObject["healthEducationGrade2"]));
field.setFontSize(9);



// PHYSICAL EDUCATION
field = pdfForm.getField("physicalEducationESLELD");
pdfFormObject["physicalEducationPlan"] === "ESLELD" ? field.check() : field.uncheck();
field = pdfForm.getField("physicalEducationIEP");
pdfFormObject["physicalEducationPlan"] === "IEP" ? field.check() : field.uncheck();
field = pdfForm.getField("physicalEducationFrench");
pdfFormObject["physicalEducationPlan"] === "French" ? field.check() : field.uncheck();

field = pdfForm.getField("physicalEducationGrade1");
field.setText(val(pdfFormObject["physicalEducationGrade1"]));
field.setFontSize(9);
field = pdfForm.getField("physicalEducationGrade2");
field.setText(val(pdfFormObject["physicalEducationGrade2"]));
field.setFontSize(9);

field = pdfForm.getField("healthAndPhysicalEducationComments");
field.setText(val(pdfFormObject["healthAndPhysicalEducationComments"]));
field.setFontSize(8);
field.enableMultiline();

// ARTS

  field = pdfForm.getField("artsHeader");
  field.setText(val(pdfFormObject["artsHeader"]));
  field.setFontSize(9);


  // ARTS 1
  field = pdfForm.getField("arts1");
  field.setText(val(pdfFormObject["arts1"]));
  field.setFontSize(8);

  field = pdfForm.getField("arts1French");
  pdfFormObject["arts1Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1ESLELD");
  pdfFormObject["arts1Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1IEP");
  pdfFormObject["arts1Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1NA");
  pdfFormObject["arts1Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts1Grade1");
    field.setText(val(pdfFormObject["arts1Grade1"]));
    field.setFontSize(9);
    field = pdfForm.getField("arts1Grade2");
    field.setText(val(pdfFormObject["arts1Grade2"]));
    field.setFontSize(9);

  field = pdfForm.getField("arts2");
  field.setText(val(pdfFormObject["arts2"]));
  field.setFontSize(8);

  field = pdfForm.getField("arts2French");
  pdfFormObject["arts2Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2ESLELD");
  pdfFormObject["arts2Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2IEP");
  pdfFormObject["arts2Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2NA");
  pdfFormObject["arts2Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts2Grade1");
    field.setText(val(pdfFormObject["arts2Grade1"]));
    field.setFontSize(9);
    field = pdfForm.getField("arts2Grade2");
    field.setText(val(pdfFormObject["arts2Grade2"]));
    field.setFontSize(9);

  field = pdfForm.getField("arts3");
  field.setText(val(pdfFormObject["arts3"]));
  field.setFontSize(8);
  field = pdfForm.getField("arts3French");
  pdfFormObject["arts3Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3ESLELD");
  pdfFormObject["arts3Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3IEP");
  pdfFormObject["arts3Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3NA");
  pdfFormObject["arts3Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts3Grade1");
    field.setText(val(pdfFormObject["arts3Grade1"]));
    field.setFontSize(9);
    field = pdfForm.getField("arts3Grade2");
    field.setText(val(pdfFormObject["arts3Grade2"]));
    field.setFontSize(9);

  field = pdfForm.getField("arts4");
  field.setText(val(pdfFormObject["arts4"]));
  field.setFontSize(8);
  field = pdfForm.getField("arts4French");
  pdfFormObject["arts4Plan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4ESLELD");
  pdfFormObject["arts4Plan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4IEP");
  pdfFormObject["arts4Plan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4NA");
  pdfFormObject["arts4Plan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("arts4Grade1");
    field.setText(val(pdfFormObject["arts4Grade1"]));
    field.setFontSize(9);
    field = pdfForm.getField("arts4Grade2");
    field.setText(val(pdfFormObject["arts4Grade2"]));
    field.setFontSize(9);



  field = pdfForm.getField("artsComments");
  field.setText(val(pdfFormObject["artsComments"]));
  field.setFontSize(8);
  field.enableMultiline();

  field = pdfForm.getField("optionalStudies");
  field.setText(val(pdfFormObject["optionalStudies"]));
  field.setFontSize(9);
  field = pdfForm.getField("optionalStudiesFrench");
  pdfFormObject["optionalStudiesPlan"] === "French" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesESLELD");
  pdfFormObject["optionalStudiesPlan"] === "ESLELD" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesIEP");
  pdfFormObject["optionalStudiesPlan"] === "IEP" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesNA");
  pdfFormObject["optionalStudiesPlan"] === "NA" ? field.check() : field.uncheck();
  field = pdfForm.getField("optionalStudiesGrade1");
    field.setText(val(pdfFormObject["optionalStudiesGrade1"]));
    field.setFontSize(9);
    field = pdfForm.getField("optionalStudiesGrade2");
    field.setText(val(pdfFormObject["optionalStudiesGrade2"]));
    field.setFontSize(9);
    field = pdfForm.getField("optionalStudiesComments");
    field.setText(val(pdfFormObject["optionalStudiesComments"]));
    field.setFontSize(8);
    field.enableMultiline();

  // ERS Section
  field = pdfForm.getField("ersCompletedYes");
  pdfFormObject["ersCompleted"] === "Yes" ? field.check() : field.uncheck();
  field = pdfForm.getField("ersCompletedNo");
  pdfFormObject["ersCompleted"] === "No" ? field.check() : field.uncheck();
  field = pdfForm.getField("ersCompletedNA");
  pdfFormObject["ersCompleted"] === "NA" ? field.check() : field.uncheck();

  field = pdfForm.getField("ersDateYear");
  field.setText(val(pdfFormObject["ersDateYear"]));
  field = pdfForm.getField("ersDateMonth");
  field.setText(val(pdfFormObject["ersDateMonth"]));
  field = pdfForm.getField("ersDateDay");
  field.setText(val(pdfFormObject["ersDateDay"]));

  field = pdfForm.getField("ersBenchmarkYes");
  pdfFormObject["ersBenchmark"] === "Yes" ? field.check() : field.uncheck();
  field = pdfForm.getField("ersBenchmarkNo");
  pdfFormObject["ersBenchmark"] === "No" ? field.check() : field.uncheck();

  // FOURTH  PAGE
  field = pdfForm.getField("studentName4");
  field.setText(val(pdfFormObject["studentName"]));
  field.setFontSize(9);
  field = pdfForm.getField("oen4");
  field.setText(val(pdfFormObject["oen"]));
  field.setFontSize(9);
  field = pdfForm.getField("grade4");
  field.setText(val(pdfFormObject["grade"]));
  field.setFontSize(9);


  // TODO (unsupported ) field = pdfForm.getField( teacherSignature
  // TODO (unsupported ) field = pdfForm.getField( principalSignature


  // TOD0: add strengths and next steps field for subject improvment field
  const __unsupportedHL = collectUnsupportedHighlights(pdfDoc, pdfForm);
  pdfForm.flatten();

  if (highlightRect) {
    const { pageIndex, x, y, width, height } = highlightRect;
    const pages = pdfDoc.getPages();
    if (pages[pageIndex]) {
      pages[pageIndex].drawRectangle({
        x: x - 2,
        y: y - 2,
        width: width + 4,
        height: height + 4,
        color: rgb(0, 0.55, 0),
        opacity: 0.25,
        borderColor: rgb(0.9, 0.7, 0),
        borderWidth: 0,
        borderOpacity: 0.9,
      });
    }
  }

  drawUnsupportedHighlights(pdfDoc, __unsupportedHL);

  // Class logos: drawn last so they sit on top of the flattened template.
  await drawLogos(pdfDoc, logos);

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const filledPDFUrl = URL.createObjectURL(blob);
  return filledPDFUrl;
};

export { fill };
